// englishcontent3.js — Section 3: Reading and Writing Fiction
// Improved full section:
// - Stronger KS3 → GCSE bridge
// - Clearer AO-style analytical language
// - More consistent topic structure
// - Adds keyTerms, examinerTip, commonMistakes and grade9Move
// - Keeps the same export shape used by the existing app

const makeTopic = (topic) => ({
  duration: '30-40 mins',
  xp: 100,
  difficulty: 'foundation',
  ...topic,
});

const question = (questionText, options, correct, explanation = '') => ({
  type: 'question',
  question: questionText,
  options,
  correct,
  ...(explanation ? { explanation } : {}),
});

const mcq = (questionText, options, correct, explanation = '') => ({
  type: 'mcq',
  question: questionText,
  options,
  correct,
  ...(explanation ? { explanation } : {}),
});

const tf = (questionText, correct, explanation = '') => ({
  type: 'true-false',
  question: questionText,
  correct,
  ...(explanation ? { explanation } : {}),
});

const tier = (label, text, critique) => ({
  label,
  text,
  critique,
});

const analysisSuccess = [
  'Makes a clear point linked to the question',
  'Uses short, relevant evidence',
  'Explains the writer’s method and effect',
  'Links to theme, purpose, context or audience where appropriate',
];

const creativeSuccess = [
  'Uses deliberate vocabulary choices',
  'Controls sentence structure for effect',
  'Creates a clear mood or atmosphere',
  'Shapes the writing for the intended reader or audience',
];

export const englishContent3 = {
  id: 3,
  title: 'Reading and Writing Fiction',
  slug: 'reading-and-writing-fiction',
  color: 'emerald',
  icon: 'BookOpen',
  description:
    'Fiction, poetry and plays — novels, narrative, characterisation, mood, poetic form, stagecraft and exam-ready analytical writing.',
  topics: [
    makeTopic({
      id: 'eng-3-1',
      slug: 'setting-and-theme',
      title: 'Setting and Theme',
      summary: 'Understand where a story happens and what it is really about.',

      keyTerms: [
        'setting',
        'theme',
        'atmosphere',
        'context',
        'symbolism',
        'motif',
      ],

      explainer: {
        title: 'Setting and Theme',
        content: `
Setting is where and when a story happens. It can be a real place, an imagined place, a historical period, a social environment, or even a symbolic space.

Writers use setting to:
- Create atmosphere, such as danger, peace, isolation or excitement
- Reflect a character's emotional state
- Hint at the type of story being told
- Suggest social, historical or cultural context
- Create contrast between different parts of the text

Theme is what a text is really about beneath the plot. Plot is what happens. Theme is what the writer is exploring through what happens.

Common themes include:
- Love
- Power
- Ambition
- Class
- Gender
- Justice
- Growing up
- Isolation
- Conflict
- Loss
- Identity

A text usually has several themes. Strong analysis links setting and theme together. For example, a dark, isolated house may create a gothic atmosphere while developing themes of fear, secrecy and imprisonment.

To analyse setting and theme, use this pattern:
1. Identify the setting.
2. Name the atmosphere it creates.
3. Link it to a theme.
4. Explain what the writer may be suggesting.
`,
      },

      interactiveLesson: {
        title: 'Setting or Theme?',
        steps: [
          {
            type: 'info',
            content:
              'Decide whether each example is mainly describing setting or theme. Then think about how they could link together.',
          },
          question(
            '"A cold, crumbling castle on a lonely moor."',
            ['Setting', 'Theme'],
            0,
            'This describes where the story happens and creates a gothic atmosphere.'
          ),
          question(
            '"The novel explores ambition and what it costs."',
            ['Setting', 'Theme'],
            1,
            'This is a repeated idea beneath the plot.'
          ),
          question(
            '"A bright, noisy marketplace in Victorian London."',
            ['Setting', 'Theme'],
            0,
            'This gives place, time period and social atmosphere.'
          ),
          question(
            '"The writer questions whether justice is possible in an unequal society."',
            ['Setting', 'Theme'],
            1,
            'This is a bigger idea or message developed across the text.'
          ),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
The ruined abbey stood at the edge of the village, its broken windows staring into the midnight rain.

Question:
How does the writer use setting to create meaning?

Model answer:
The writer uses the gothic setting of the "ruined abbey" to create a threatening atmosphere. The adjective "ruined" suggests decay and damage, which may reflect a wider theme of collapse or corruption. The personification of the windows "staring" makes the building seem alive, as if it is watching the village. This links the setting to themes of fear, secrecy and the supernatural.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'The ruined abbey creates a scary atmosphere. The word "ruined" suggests the place is old and damaged.',
          'This is clear and relevant, but it needs a stronger link to theme and writer purpose.'
        ),
        tier(
          'Strong',
          'The writer uses the "ruined abbey" to create a gothic atmosphere. The adjective "ruined" suggests decay, which links to the theme of something damaged or corrupted.',
          'This links setting to theme and begins to analyse vocabulary.'
        ),
        tier(
          'Grade 9 move',
          'The personification of the windows "staring" turns the setting into an active presence, making the abbey feel threatening rather than simply old. The writer uses the setting symbolically, suggesting that the past still watches and controls the present.',
          'This moves beyond identification into symbolic interpretation.'
        ),
      ],

      practice: [
        mcq('Which of these is a theme?', ['Ambition', 'A lighthouse on a cliff'], 0),
        mcq('Setting often helps create:', ['Atmosphere and mood', 'Only the word count'], 0),
        tf('A text can only have one theme.', false),
        tf('Themes can be linked to context.', true),
        mcq(
          'Which sentence links setting and theme most effectively?',
          [
            'The stormy sea reflects the character’s inner conflict.',
            'The sea is wet and blue.',
          ],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Describe a setting and explain one theme a writer might explore using it.
`,
        sentenceStarter: 'The setting is...',
        hints: [
          'Name a specific place and time.',
          'Choose a clear atmosphere.',
          'Link your setting to a theme like fear, hope, power or isolation.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Describes a clear setting',
          'Names a theme',
          'Explains the atmosphere',
          'Links setting to theme or writer purpose',
        ],
      },

      examinerTip:
        'Do not stop at “the setting is scary”. Explain how specific details make it scary and what bigger idea that fear supports.',

      commonMistakes: [
        'Confusing theme with plot',
        'Describing the setting without analysing its effect',
        'Forgetting to mention the writer’s purpose',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your answer by:
- adding one precise adjective or sensory detail
- naming the atmosphere
- explaining how the setting supports your chosen theme
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might two writers choose very different settings to explore the same theme?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-2',
      slug: 'characterisation',
      title: 'Characterisation',
      summary: 'How authors present characters to the reader.',

      keyTerms: [
        'characterisation',
        'protagonist',
        'antagonist',
        'dialogue',
        'motivation',
        'development',
      ],

      explainer: {
        title: 'Characterisation',
        content: `
Characterisation is the way a writer presents a character.

Writers reveal characters through:
- Direct description from the narrator
- Dialogue: what the character says
- Action: what the character does
- Reaction: how other characters respond to them
- Thoughts and feelings
- Appearance, clothing and body language
- Repeated patterns of behaviour
- Changes over time

The protagonist is usually the main character. The antagonist is the opposing force. This does not always mean "hero" and "villain". Sometimes the antagonist is society, fear, poverty, prejudice, illness or a character's own weakness.

When analysing characterisation, avoid saying only "this shows he is nice" or "this shows she is evil". Stronger analysis explains:
- What trait is being presented
- How the writer presents it
- Why the writer wants the reader to notice it
- How the character connects to a theme

A powerful analytical sentence might be:
The writer characterises Scrooge as emotionally isolated through the phrase "solitary as an oyster", using simile to suggest he is closed off from society.
`,
      },

      interactiveLesson: {
        title: 'How Characters Are Built',
        steps: [
          question(
            'Which of these shows characterisation through dialogue?',
            ['"I will not be silenced," she whispered.', 'The house was cold.'],
            0
          ),
          question('The protagonist is usually:', ['The main character', 'The rhyme scheme'], 0),
          question(
            '"Everyone in the village called him generous." This presents character through:',
            ['What other characters say about them', 'Weather description'],
            0
          ),
          question(
            'A character’s motivation means:',
            ['What drives their choices', 'The length of the chapter'],
            0
          ),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
Mara stepped between the younger child and the snarling dog. Her voice shook, but she did not move.

Question:
How does the writer present Mara?

Model answer:
The writer presents Mara as frightened but brave. The action "stepped between" shows that she is willing to protect someone weaker than herself. However, the detail that her "voice shook" prevents her from seeming fearless. Instead, the writer suggests real courage: Mara is afraid, but chooses to act anyway.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'Mara is brave because she stands in front of the dog.',
          'This makes a clear point but needs closer evidence and explanation.'
        ),
        tier(
          'Strong',
          'The writer presents Mara as protective through the action "stepped between", which shows she physically places herself in danger for someone else.',
          'This analyses action and links it to character.'
        ),
        tier(
          'Grade 9 move',
          'The contrast between her shaking voice and her refusal to move presents courage as a conscious moral choice rather than a lack of fear.',
          'This interprets the complexity of the character.'
        ),
      ],

      practice: [
        mcq(
          'Which method of characterisation is "the narrator describes their long scar"?',
          ['Narrator description', 'Dialogue'],
          0
        ),
        tf('All characters exist purely for decoration.', false),
        tf('A character can change during a story.', true),
        mcq(
          'Which is the strongest analytical phrase?',
          [
            'The verb "snapped" suggests his anger is sudden and aggressive.',
            'The word is good.',
          ],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Pick a character you know from any novel, film or game and explain one method the writer uses to present them.
`,
        sentenceStarter: 'The writer presents ___ through...',
        hints: [
          'Use one method: dialogue, action, description, thoughts or others’ reactions.',
          'Give a short example.',
          'Explain the reader’s impression.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Names a method of characterisation',
          'Gives an example',
          'Explains the effect on the reader',
          'Links to a character trait or theme',
        ],
      },

      examinerTip:
        'The best character paragraphs often notice contradiction: a character may appear confident but act insecure, or speak kindly while behaving cruelly.',

      commonMistakes: [
        'Calling every character either good or bad',
        'Retelling what the character does without analysing how they are presented',
        'Forgetting that characters can change',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your answer by zooming in on one verb, adjective or line of dialogue and explaining what it reveals about the character.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is characterisation through action often more powerful than direct description?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-3',
      slug: 'language-in-fiction-texts',
      title: 'Language in Fiction Texts',
      summary: 'Explore dialogue, figurative language, symbolism, inference and archaic style.',

      keyTerms: [
        'imagery',
        'metaphor',
        'simile',
        'personification',
        'symbolism',
        'inference',
        'semantic field',
        'archaic language',
      ],

      explainer: {
        title: 'Language in Fiction Texts',
        content: `
Writers choose language carefully because individual words shape meaning.

Important language features include:
- Dialogue: what characters say and how they say it
- Figurative language: simile, metaphor and personification
- Imagery: descriptive language that creates a sensory picture
- Symbolism: an object, place or action that represents a bigger idea
- Inference: meaning that is suggested rather than directly stated
- Semantic field: a group of words linked by meaning
- Archaic language: old-fashioned words or sentence patterns
- Dialect or non-standard grammar: language that may reveal setting, class, identity or voice

To analyse language, do not simply name a technique. Use the technique to explain meaning.

Weak:
The writer uses a metaphor.

Better:
The metaphor "the city was a furnace" suggests the setting is oppressive and unbearable, making the reader feel trapped alongside the character.

A strong language paragraph often zooms in on one word. Ask:
- Why this word?
- What are its connotations?
- How does it affect the reader?
- How does it link to the writer’s purpose?
`,
      },

      interactiveLesson: {
        title: 'Language in Fiction',
        steps: [
          question('"Her eyes were a stormy sea." This is:', ['A metaphor', 'A statistic'], 0),
          question('A broken chain could symbolise:', ['Freedom or loss', 'A paragraph break'], 0),
          question(
            '"Hark, what light through yonder window breaks?" is an example of:',
            ['Archaic language', 'Modern slang'],
            0
          ),
          question(
            'A semantic field is:',
            ['A group of words linked by meaning', 'A list of characters'],
            0
          ),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
She wore a black dress that swallowed the light.

Question:
How does the writer use language to create unease?

Model answer:
The writer creates unease through the metaphor "swallowed the light". The verb "swallowed" makes the dress seem alive and predatory, as if it consumes brightness. This gives the character a threatening presence and creates a gothic atmosphere.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'The dress is black, which makes it seem scary.',
          'This identifies a colour association but needs more precise language analysis.'
        ),
        tier(
          'Strong',
          'The verb "swallowed" personifies the dress, making it seem alive and threatening.',
          'This zooms in on a specific word and explains effect.'
        ),
        tier(
          'Grade 9 move',
          'Because the dress "swallowed the light", the character appears to absorb safety and hope from the room, suggesting she is a corrupting presence.',
          'This moves from word analysis into symbolic meaning.'
        ),
      ],

      practice: [
        mcq('Dialect in dialogue can tell the reader about:', ['Setting or social identity', 'Page numbers'], 0),
        tf('Symbolism is when an object stands for a bigger idea.', true),
        tf('Inference means the writer states everything directly.', false),
        mcq(
          'Which is the best zoom-in?',
          [
            'The verb "lurched" suggests awkward, uncontrolled movement.',
            'The sentence has words.',
          ],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"The candle guttered and the shadows stretched along the wall."

Explain the effect of the writer's language on the reader.
`,
        sentenceStarter: 'The writer creates...',
        hints: [
          'Pick one word and comment on its connotations.',
          'Name a technique if you can.',
          'Say what feeling it creates.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses a short quotation',
          'Names or describes a technique',
          'Zooms in on one word',
          'Explains the effect on the reader',
        ],
      },

      examinerTip:
        'Technique spotting is not analysis. The mark comes from explaining how the technique shapes meaning.',

      commonMistakes: [
        'Listing techniques without explanation',
        'Using quotations that are too long',
        'Saying “this makes the reader want to read on” for every effect',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your answer by adding:
- one quoted word
- one connotation
- one effect on the reader
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might a writer deliberately use old-fashioned language even in a modern novel?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-4',
      slug: 'mood',
      title: 'Mood',
      summary: 'How a text makes the reader feel.',

      keyTerms: [
        'mood',
        'atmosphere',
        'tone',
        'tension',
        'suspense',
        'pace',
        'sensory imagery',
      ],

      explainer: {
        title: 'Mood',
        content: `
Mood is the emotional atmosphere of a text. It is how the writing makes the reader feel.

Mood is not exactly the same as tone:
- Mood = the feeling created for the reader
- Tone = the writer or narrator’s attitude

Common moods include:
- Tense
- Peaceful
- Melancholy
- Joyful
- Threatening
- Eerie
- Hopeful
- Claustrophobic
- Nostalgic

Writers create mood through:
- Word choice
- Setting details
- Sensory imagery
- Sentence length
- Punctuation
- Pace
- Repetition
- Sound
- Contrast

Short sentences can create panic, shock or tension. Long flowing sentences can create calm, beauty or overwhelming detail. Ellipses can create suspense. A sudden shift in mood often signals that something important is about to happen.

When analysing mood, use precise emotional vocabulary. Instead of "scary", try "eerie", "threatening", "claustrophobic" or "unsettling".
`,
      },

      interactiveLesson: {
        title: 'Mood',
        steps: [
          question(
            '"The door creaked... he held his breath..." The mood is:',
            ['Tense', 'Cheerful'],
            0
          ),
          question(
            '"Sunlight poured through the window and the garden hummed with bees." The mood is:',
            ['Peaceful', 'Threatening'],
            0
          ),
          question('Short, sharp sentences usually create:', ['Tension', 'Calm'], 0),
          question(
            'Which word is more precise than "sad"?',
            ['Melancholy', 'Big'],
            0
          ),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
He stopped. Listened. Nothing moved. Then — a tap on the window.

Question:
How does the writer create a tense mood?

Model answer:
The writer creates a tense mood through short, fragmented sentences. "He stopped. Listened." slows the pace and makes the reader focus on each tiny action. The dash before "a tap on the window" creates a sudden interruption, making the sound feel shocking and threatening.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'The mood is tense because something taps on the window.',
          'This identifies the mood but needs method.'
        ),
        tier(
          'Strong',
          'The short sentences slow the pace, making the reader wait nervously for what will happen next.',
          'This links structure to reader effect.'
        ),
        tier(
          'Grade 9 move',
          'The silence before the tap creates a moment of suspended expectation, so the smallest sound becomes magnified and threatening.',
          'This explains how tension is built through contrast.'
        ),
      ],

      practice: [
        mcq(
          '"The waves lapped gently and the gulls called in the distance" creates a mood of:',
          ['Peace', 'Horror'],
          0
        ),
        mcq('Ellipses (...) are often used to build:', ['Suspense', 'Certainty'], 0),
        tf('Mood and tone are exactly the same thing.', false),
        mcq(
          'Which word best describes an uncomfortable, enclosed mood?',
          ['Claustrophobic', 'Celebratory'],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write 2–3 sentences describing an empty classroom in a way that creates a tense mood.
`,
        sentenceStarter: 'The classroom was...',
        hints: [
          'Use at least one short sentence.',
          'Choose verbs that suggest unease.',
          'Add one sound or small movement.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses carefully chosen vocabulary',
          'Varies sentence length',
          'Creates a clear tense mood',
          'Includes a sensory detail',
        ],
      },

      examinerTip:
        'Mood questions are often really method questions. Always explain how vocabulary, sentence structure or setting detail creates the feeling.',

      commonMistakes: [
        'Using vague mood words',
        'Ignoring sentence structure',
        'Describing what happens instead of how it feels',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your answer by replacing any vague mood word with a more precise one, such as eerie, oppressive, nostalgic, fragile or threatening.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
How can a writer change the mood of a passage without changing the setting or characters?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-5',
      slug: 'characters-in-novels',
      title: 'Characters in Novels',
      summary: 'How characters drive plot and meaning.',

      keyTerms: [
        'protagonist',
        'antagonist',
        'dynamic character',
        'static character',
        'foil',
        'character arc',
      ],

      explainer: {
        title: 'Characters in Novels',
        content: `
Characters in novels are central to plot, theme and reader response.

A character may:
- Drive the plot forward
- Reveal a theme
- Represent a social group or idea
- Create conflict
- Change across the novel
- Make the reader feel sympathy, fear, anger or admiration

Important character types:
- Protagonist: the main character or central figure
- Antagonist: a person or force opposing the protagonist
- Dynamic character: a character who changes
- Static character: a character who stays mostly the same
- Foil: a character who contrasts with another character to highlight their traits

A strong GCSE-style paragraph does not just describe a character. It explains how the writer uses that character to develop the novel’s meaning.

Example:
Dickens uses Scrooge’s transformation to present the possibility of moral redemption. His change from isolation to generosity allows Dickens to criticise selfishness in Victorian society.
`,
      },

      interactiveLesson: {
        title: 'Character Purpose',
        steps: [
          question('A character who opposes the protagonist is the:', ['Antagonist', 'Narrator'], 0),
          question('A character who changes during a novel is called:', ['A dynamic character', 'A flat character'], 0),
          question(
            'Why do writers include minor characters?',
            ['To reveal ideas, contrast characters or move the plot', 'Only to pad the word count'],
            0
          ),
          question(
            'A foil character is used to:',
            ['Create contrast with another character', 'Explain punctuation'],
            0
          ),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
In Great Expectations, Pip begins as a humble orphan and later becomes a proud young gentleman, before eventually learning humility again.

Model answer:
Dickens uses Pip as a dynamic character whose changes reflect the novel's themes of class and moral growth. His shift from humility to pride and back again allows Dickens to show how social ambition can distort a person. Pip’s character arc therefore becomes a way of criticising a society that links worth too closely to status.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'Pip changes in the novel and learns to be humble.',
          'This is accurate but needs a link to writer purpose.'
        ),
        tier(
          'Strong',
          'Pip’s change from humility to pride shows how ambition can damage a person’s values.',
          'This links character development to theme.'
        ),
        tier(
          'Grade 9 move',
          'Dickens makes Pip’s moral education structural: the reader grows in understanding as Pip does, which turns his character arc into a critique of class aspiration.',
          'This links character, structure and social criticism.'
        ),
      ],

      practice: [
        mcq(
          'Which of these statements is true of a strong character?',
          ['They have a clear purpose', 'They exist only for decoration'],
          0
        ),
        tf('Minor characters can reveal things about the protagonist.', true),
        tf('A protagonist is always morally perfect.', false),
        mcq('A character arc is:', ['How a character changes over time', 'A type of punctuation'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Pick a character from any novel and explain their purpose in the story.
`,
        sentenceStarter: 'The writer uses ___ to...',
        hints: [
          'Say whether the character changes.',
          'Link the character to a theme.',
          'Explain how the reader is meant to respond.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Identifies a clear purpose for the character',
          'Links the character to a theme or the plot',
          'Explains whether the character changes',
          'Comments on reader response',
        ],
      },

      examinerTip:
        'A character is not just a person in the story. In analysis, treat them as a deliberate construction used by the writer.',

      commonMistakes: [
        'Retelling the character’s life story',
        'Forgetting minor characters can be important',
        'Ignoring how characters change across the whole text',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence beginning: "The writer may use this character to suggest..."
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is a character who changes often more memorable than one who stays the same?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-6',
      slug: 'structure-of-novels',
      title: 'Structure of Novels',
      summary: 'Linear and non-linear plots, foreshadowing, flashbacks and turning points.',

      keyTerms: [
        'structure',
        'linear',
        'non-linear',
        'foreshadowing',
        'flashback',
        'flashforward',
        'turning point',
        'cliffhanger',
        'pace',
      ],

      explainer: {
        title: 'Structure of Novels',
        content: `
Structure is the order and organisation of a text.

In novels, structure can include:
- The order of events
- Chapter endings
- Shifts in time
- Changes in viewpoint
- Repeated motifs
- Turning points
- Beginnings and endings
- Pace
- Contrasts between scenes

Linear structure means events are told in chronological order.

Non-linear structure means events are told out of order. This might include:
- Flashback: moving back to an earlier event
- Flashforward: jumping ahead to a later event
- Framing narrative: one story inside another story
- Circular structure: ending where the text began

Key structural techniques:
- Foreshadowing: hints about future events
- Cliffhanger: unresolved tension at the end of a chapter
- Turning point: a moment that changes the direction of the story
- Repetition: repeated words, images or situations that connect ideas

When analysing structure, focus on why the writer places information where they do.
`,
      },

      interactiveLesson: {
        title: 'Plot Structure',
        steps: [
          question('A plot where events happen in time order is:', ['Linear', 'Non-linear'], 0),
          question('A clue about what is going to happen later is:', ['Foreshadowing', 'A flashback'], 0),
          question('An exciting unresolved ending to a chapter is:', ['A cliffhanger', 'A flashforward'], 0),
          question('A circular structure means:', ['The ending echoes the beginning', 'There are no chapters'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
Mary Shelley's Frankenstein opens with letters written after some of the main events, before moving back to Frankenstein's childhood.

Model answer:
Shelley uses a non-linear structure and a framing narrative. By beginning with letters from Walton, she creates mystery before the reader fully understands Victor Frankenstein’s story. This delayed explanation makes the reader question how Victor reached such a desperate state, building suspense and moral curiosity.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'The story starts later and then goes back in time.',
          'This identifies structure but needs effect.'
        ),
        tier(
          'Strong',
          'The non-linear opening creates mystery because the reader sees the consequences before learning the causes.',
          'This explains effect clearly.'
        ),
        tier(
          'Grade 9 move',
          'The framing structure makes the novel feel like a chain of warnings, as one narrator passes dangerous knowledge to another.',
          'This links structure to theme and purpose.'
        ),
      ],

      practice: [
        mcq(
          '"As he opened the letter, he had no idea it would ruin his life." This is:',
          ['Foreshadowing', 'A cliffhanger'],
          0
        ),
        tf('A flashback moves the action backwards in time.', true),
        tf('Structure only means paragraph length.', false),
        mcq(
          'A turning point is:',
          ['A moment that changes the direction of the story', 'A type of rhyme'],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Explain one structural technique and how it keeps a reader interested.
`,
        sentenceStarter: 'The writer uses...',
        hints: [
          'Name one technique.',
          'Explain what it does to the reader.',
          'Link it to suspense, mystery or understanding.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Names a structural technique',
          'Explains its effect on the reader',
          'Links structure to meaning',
        ],
      },

      examinerTip:
        'For structure, ask: why here? Why now? Why reveal this information at this point in the text?',

      commonMistakes: [
        'Only analysing language in a structure question',
        'Calling every ending a cliffhanger',
        'Forgetting beginnings and endings are structural choices',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your answer by adding a sentence that begins: "The placement of this moment is important because..."
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might a writer choose a non-linear structure for a story about trauma or memory?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-7',
      slug: 'narrative-in-novels',
      title: 'Narrative in Novels',
      summary: 'First-person, third-person, limited, omniscient and unreliable narrators.',

      keyTerms: [
        'narrator',
        'viewpoint',
        'first person',
        'third person',
        'limited narrator',
        'omniscient narrator',
        'unreliable narrator',
        'bias',
      ],

      explainer: {
        title: 'Narrative in Novels',
        content: `
The narrator is the voice telling the story. Writers choose narrators carefully because viewpoint controls what the reader knows, feels and trusts.

Main narrative viewpoints:
- First person: "I", "we"
- Third person: "he", "she", "they"

A first-person narrator can feel intimate and personal, but their viewpoint is limited. The reader only knows what that narrator knows or chooses to reveal.

Third-person narrators can be:
- Limited: focused mainly on one character’s thoughts and feelings
- Omniscient: all-knowing, with access to multiple characters and events

An unreliable narrator gives an account that may be biased, mistaken, incomplete or deliberately misleading.

When analysing narrative viewpoint, ask:
- Who is telling the story?
- What do they know?
- What do they hide or misunderstand?
- How does the viewpoint affect the reader’s judgement?
`,
      },

      interactiveLesson: {
        title: 'Types of Narrator',
        steps: [
          question('"I watched the door close" uses which viewpoint?', ['First-person', 'Third-person'], 0),
          question(
            "A narrator who knows every character's thoughts is:",
            ['Omniscient', 'Limited'],
            0
          ),
          question('An unreliable narrator:', ['Gives a biased or misleading account', 'Always tells the truth'], 0),
          question(
            'A limited third-person narrator usually follows:',
            ['One character closely', 'Every character equally'],
            0
          ),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
In Wuthering Heights, much of the story is told through Nelly Dean, a housekeeper who was present for many events.

Model answer:
Brontë uses Nelly as a first-person narrator, which makes the story feel personal and detailed. However, because Nelly has her own opinions and loyalties, the reader must question her reliability. This narrative choice creates uncertainty, encouraging the reader to judge events rather than passively accept them.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'Nelly tells the story in first person, so we hear her view.',
          'This is accurate but needs an effect.'
        ),
        tier(
          'Strong',
          'Because Nelly is close to the events, her narration feels intimate, but her personal opinions may affect how the reader sees other characters.',
          'This explains benefit and limitation.'
        ),
        tier(
          'Grade 9 move',
          'Brontë turns narration into interpretation: the reader is not simply told a story, but forced to test the reliability of the storyteller.',
          'This links viewpoint to the reader’s role.'
        ),
      ],

      practice: [
        mcq('Which pronouns does a third-person narrator use?', ['He, she, they', 'I, we'], 0),
        tf('An omniscient narrator is limited to one character.', false),
        tf('A first-person narrator may be biased.', true),
        mcq(
          'Which viewpoint usually feels most personal?',
          ['First person', 'A contents page'],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Explain one advantage of a first-person narrator and one advantage of an omniscient third-person narrator.
`,
        sentenceStarter: 'A first-person narrator is useful because...',
        hints: [
          'Think about intimacy and limitation.',
          'Think about wider knowledge.',
          'Use the word viewpoint.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Identifies one benefit of first person',
          'Identifies one benefit of omniscient narration',
          'Explains how viewpoint affects the reader',
        ],
      },

      examinerTip:
        'Narrative viewpoint is a method. Link it to trust, intimacy, suspense or reader judgement.',

      commonMistakes: [
        'Assuming the narrator is always the writer',
        'Forgetting first-person narrators can be unreliable',
        'Using viewpoint terms without explaining effect',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining what the reader knows, does not know, or cannot fully trust because of the chosen narrator.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might an unreliable narrator create a more interesting reading experience?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-8',
      slug: 'answering-questions-about-novels',
      title: 'Answering Questions About Novels',
      summary: 'Structure strong analytical answers using point, evidence and explanation.',

      keyTerms: [
        'point',
        'evidence',
        'explanation',
        'embedded quotation',
        'context',
        'writer’s purpose',
        'analysis',
      ],

      explainer: {
        title: 'Answering Questions About Novels',
        content: `
Novel questions usually ask how the writer presents a character, theme, setting, relationship or idea.

A strong paragraph uses:
- Point: a clear answer to the question
- Evidence: a short quotation or precise reference
- Explain: analysis of method, meaning and effect

A better GCSE paragraph also includes:
- A technique or method
- A zoom-in on one important word
- A link to the writer’s purpose
- A link to context where relevant
- A clear connection back to the question

Avoid:
- Retelling the plot
- Starting with "This quote shows"
- Using very long quotations
- Naming techniques without explaining them
- Making claims without evidence

Useful sentence frame:
The writer presents ___ as ___ through the phrase "__". The word "__" suggests ___, making the reader ___. This may link to the wider theme of ___ because ___.
`,
      },

      interactiveLesson: {
        title: 'Writing Strong Answers',
        steps: [
          question('The first part of PEE stands for:', ['Point', 'Paragraph'], 0),
          question('The best evidence in an analytical answer is:', ['A short, relevant quotation', 'A long copied paragraph'], 0),
          question('Top-mark answers usually:', ['Link back to the question', 'Ignore the question'], 0),
          question(
            'Zooming in means:',
            ['Analysing one word or short phrase closely', 'Writing in tiny handwriting'],
            0
          ),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does the writer present the character's loneliness?

Text:
She sat alone at the long table, her soup growing cold in front of her.

Model answer:
The writer presents the character's loneliness through the isolating setting. The phrase "alone at the long table" emphasises how disproportionate her solitude feels: the table is built for many people, but only she is there. The detail of soup "growing cold" subtly shows she has lost interest in ordinary comfort, suggesting emotional emptiness.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'She is lonely because she is alone at the table.',
          'This answers the question but is too simple.'
        ),
        tier(
          'Strong',
          'The phrase "alone at the long table" emphasises her isolation because the size of the table makes her loneliness feel larger.',
          'This explains the image and effect.'
        ),
        tier(
          'Grade 9 move',
          'The cold soup becomes a quiet symbol of emotional neglect: even nourishment is left untouched, implying loneliness has affected her will to engage with life.',
          'This interprets detail symbolically.'
        ),
      ],

      practice: [
        mcq('A good analytical paragraph usually starts with:', ['A clear point', 'A long quotation'], 0),
        tf('Linking to context can improve an answer.', true),
        tf('Plot summary is the same as analysis.', false),
        mcq(
          'Which quotation is better for analysis?',
          ['"growing cold"', 'The whole paragraph copied out'],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"He walked past the shop window, avoiding his own reflection."

Write a short analytical paragraph exploring what the writer presents about this character.
`,
        sentenceStarter: 'The writer presents...',
        hints: [
          'Make one clear point.',
          'Embed a short quotation.',
          'Explain what the language suggests.',
          'Zoom in on "avoiding" or "reflection".',
        ],
      },

      aiFeedback: {
        successCriteria: analysisSuccess,
      },

      examinerTip:
        'The best analytical paragraphs do not need lots of quotations. One short quotation analysed well is often stronger than three undeveloped ones.',

      commonMistakes: [
        'Beginning with evidence instead of a point',
        'Using quotations that are too long',
        'Not explaining the effect of the chosen word',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your paragraph by adding:
- one technique or method
- one zoom-in word
- one sentence about the writer’s purpose
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is zooming in on one word often more powerful than analysing a whole sentence?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-9',
      slug: 'writing-stories',
      title: 'Writing Stories',
      summary: 'Plan, open strongly and finish memorably.',

      keyTerms: [
        'hook',
        'conflict',
        'resolution',
        'viewpoint',
        'description',
        'dialogue',
        'motif',
        'cyclical ending',
      ],

      explainer: {
        title: 'Writing Stories',
        content: `
Good story writing is controlled. It does not need a huge plot. In a short exam-style story, one powerful moment is usually better than a complicated adventure.

Before writing, plan:
- Main character
- Setting
- Mood
- Conflict or problem
- Turning point
- Ending

Strong openings often:
- Start in the middle of action
- Create immediate mystery
- Use an unusual image
- Begin with a short line of dialogue
- Establish mood quickly

Keep the focus narrow:
- One or two main characters
- One main setting
- One main problem
- One clear change

Use deliberate techniques:
- Varied sentence lengths
- Sensory imagery
- Figurative language
- Dialogue that reveals character
- Repeated motif
- Cyclical ending that links back to the beginning

Avoid weak endings such as "and it was all a dream" unless you do something genuinely original with the cliché.
`,
      },

      interactiveLesson: {
        title: 'Writing Stories',
        steps: [
          question(
            'Which is the strongest story opening?',
            ['"The bell rang, and then the screaming began."', '"It was a normal Tuesday morning."'],
            0
          ),
          question('A good short story usually focuses on:', ['One main problem', 'Ten subplots'], 0),
          question(
            'Which ending is a cliché you should usually avoid?',
            ['"...and it was all a dream."', 'A moment of unexpected reconciliation'],
            0
          ),
          question('A cyclical ending:', ['Echoes the opening', 'Has no punctuation'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Opening:
The last bus had gone. She stared at the empty stop, then at the fog rolling up the road.

Why it works:
The opening drops the reader into a problem immediately. The short sentence "The last bus had gone" creates urgency. The fog adds atmosphere and mystery, while the empty bus stop creates isolation. The reader wants to know what the character will do next.
`,
      },

      workedExamples: [
        tier(
          'Basic opening',
          'It was a normal day and I woke up and got dressed.',
          'This is slow and generic.'
        ),
        tier(
          'Improved opening',
          'The front door was open. It had not been open when I went to sleep.',
          'This begins with mystery and creates immediate tension.'
        ),
        tier(
          'Grade 9 move',
          'The front door breathed in the rain. It had not been open when I went to sleep.',
          'This uses personification, mood and mystery in two controlled sentences.'
        ),
      ],

      practice: [
        mcq('Planning before writing helps because:', ['It keeps the story focused', 'It slows the reader'], 0),
        tf('A good short story usually has many subplots.', false),
        tf('Dialogue should reveal character or move the story forward.', true),
        mcq(
          'Which is usually better in a short story?',
          ['One focused conflict', 'A whole lifetime of events'],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write the opening two sentences of a short story that immediately creates a mood and hints at a problem.
`,
        sentenceStarter: '',
        hints: [
          'Drop the reader into action or mystery.',
          'Use sensory detail.',
          'Avoid explaining everything at once.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Starts quickly',
          'Creates a clear mood',
          'Hints at a problem or mystery',
          'Uses controlled sentence structure',
        ],
      },

      examinerTip:
        'For exam creative writing, control beats complexity. A focused story with strong description is better than a rushed plot with too many events.',

      commonMistakes: [
        'Starting too slowly',
        'Adding too many characters',
        'Ending with a cliché',
        'Forgetting paragraphing',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Rewrite your opening by:
- cutting any unnecessary explanation
- adding one sensory image
- varying sentence length
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is a short story usually stronger when it focuses on a single problem or moment?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-10',
      slug: 'poetry-features',
      title: 'Poetry Features',
      summary: 'Lines, stanzas, rhyme and types of poem.',

      keyTerms: [
        'line',
        'stanza',
        'verse',
        'haiku',
        'ballad',
        'sonnet',
        'limerick',
        'free verse',
        'rhyme',
        'half-rhyme',
      ],

      explainer: {
        title: 'Poetry Features',
        content: `
Poetry is usually more compressed than prose. Poets often use fewer words, but those words carry more weight.

Poems are arranged into:
- Lines: single rows of words
- Stanzas or verses: groups of lines
- Rhyme schemes: patterns of end sounds
- Rhythm: patterns of beat and pace

Common forms:
- Haiku: three lines, traditionally 5-7-5 syllables
- Ballad: a narrative poem that tells a story
- Sonnet: a 14-line poem, often with a turn or volta
- Limerick: a humorous five-line poem with a strong rhythm
- Free verse: no fixed rhyme or metre

Some poems use exact rhyme. Some use half-rhyme, where sounds are similar but not identical. Some poems avoid rhyme completely.

When analysing poetry, look at:
- What the poem says
- How the poem sounds
- How the poem is shaped on the page
- Where key words are placed
`,
      },

      interactiveLesson: {
        title: 'Poem Types',
        steps: [
          question('A haiku has how many lines?', ['3', '14'], 0),
          question('A 14-line poem with a strict structure is often a:', ['Sonnet', 'Limerick'], 0),
          question('A poem without fixed rhyme or rhythm is:', ['Free verse', 'Ballad'], 0),
          question('A group of lines in a poem is called:', ['A stanza', 'A paragraph only'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
A poem is made up of four stanzas, each with four lines and an ABAB rhyme scheme.

Model answer:
The poem uses a regular structure of quatrains with an ABAB rhyme scheme. This creates a steady, predictable rhythm. Depending on the subject, this regularity could suggest calm, control, memory or tradition.
`,
      },

      practice: [
        mcq('A group of lines in a poem is called a:', ['Verse or stanza', 'Chapter'], 0),
        mcq('A half-rhyme is:', ['A similar but not identical sound', 'An exact sound match'], 0),
        tf('All poems must rhyme.', false),
        tf('The shape of a poem can affect meaning.', true),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Pick a type of poem — haiku, ballad, sonnet, limerick or free verse — and explain what makes it distinctive.
`,
        sentenceStarter: 'A ___ is distinctive because...',
        hints: [
          'Mention line count, rhyme or rhythm.',
          'Mention a typical subject or mood.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Names a poetic form',
          'Identifies a distinctive feature',
          'Links form to mood, subject or effect',
        ],
      },

      examinerTip:
        'When writing about poetry, do not only analyse imagery. Form and structure are often where the strongest interpretations are found.',

      commonMistakes: [
        'Calling every group of lines a paragraph',
        'Assuming all poetry rhymes',
        'Ignoring line breaks',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining how the poem’s form affects how the reader experiences the poem.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might a poet choose free verse instead of a strict form like a sonnet?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-11',
      slug: 'poetic-techniques',
      title: 'Poetic Techniques',
      summary: 'Imagery, repetition, onomatopoeia and more in poetry.',

      keyTerms: [
        'imagery',
        'simile',
        'metaphor',
        'personification',
        'repetition',
        'alliteration',
        'sibilance',
        'onomatopoeia',
        'symbolism',
      ],

      explainer: {
        title: 'Poetic Techniques',
        content: `
Poetry uses language in a concentrated way. A single word, sound or repeated phrase can shape the whole meaning of a poem.

Key poetic techniques:
- Imagery: language that creates a vivid picture or sensory experience
- Simile: comparison using "like" or "as"
- Metaphor: direct comparison
- Personification: giving human qualities to non-human things
- Repetition: repeating words, sounds, phrases or lines
- Alliteration: repeated initial sounds
- Sibilance: repeated soft "s" sounds
- Onomatopoeia: words that sound like what they describe
- Symbolism: concrete details representing abstract ideas

Strong analysis explains why the technique appears at that moment.

Weak:
The poet uses alliteration.

Better:
The alliteration in "silver sails sliced the sea" creates a smooth but sharp sound, making the movement seem graceful and precise.
`,
      },

      interactiveLesson: {
        title: 'Identify the Technique',
        steps: [
          question('"The wind whispered through the trees" is:', ['Personification', 'Onomatopoeia'], 0),
          question('"Crack, crack, crack went the branches" is:', ['Onomatopoeia and repetition', 'Rhyme only'], 0),
          question('"Silver sails sliced the sea" is:', ['Alliteration', 'A simile'], 0),
          question('Repeated soft "s" sounds are called:', ['Sibilance', 'Dialogue'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
The raven tapped, tapped, tapped at the window pane.

Model answer:
The repetition of "tapped" mimics the repeated sound at the window, creating a steady and unsettling rhythm. The onomatopoeic effect makes the sound feel real to the reader. Because the tapping continues, it builds dread and suggests that the speaker cannot escape the disturbance.
`,
      },

      practice: [
        mcq('"Boom" and "hiss" are examples of:', ['Onomatopoeia', 'Rhyme'], 0),
        tf('Repetition can create rhythm.', true),
        tf('A metaphor uses like or as.', false),
        mcq(
          'Which technique gives human qualities to something non-human?',
          ['Personification', 'Paragraphing'],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write a single poetic line using at least two different techniques from the list.
`,
        sentenceStarter: '',
        hints: [
          'Try alliteration and personification together.',
          'Aim for strong imagery.',
          'Read it aloud to check the sound.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses at least two poetic techniques',
          'Creates a clear image',
          'Uses sound deliberately',
          'Creates a mood',
        ],
      },

      examinerTip:
        'In poetry, sound can be meaning. Comment on how words feel when spoken aloud.',

      commonMistakes: [
        'Naming the wrong technique',
        'Explaining what the line means but not how it works',
        'Ignoring sound effects',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your line by making one sound pattern more deliberate, such as alliteration, sibilance or repetition.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why does repetition often feel more powerful in poetry than in prose?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-12',
      slug: 'rhyme',
      title: 'Rhyme',
      summary: 'Rhyme schemes, rhyming couplets and their effect.',

      keyTerms: [
        'rhyme',
        'rhyme scheme',
        'couplet',
        'alternate rhyme',
        'envelope rhyme',
        'half-rhyme',
        'irregular rhyme',
      ],

      explainer: {
        title: 'Rhyme',
        content: `
Rhyme is when words share similar end sounds. Poets use rhyme to create pattern, emphasis, memorability and musicality.

Rhyme scheme is the pattern of rhyming sounds at the ends of lines. Each new end sound gets a new letter.

Examples:
- AABB: two rhyming couplets
- ABAB: alternate rhyme
- ABBA: envelope rhyme
- ABCB: common ballad-style pattern

A rhyming couplet is two lines together that rhyme. Couplets often create a neat, memorable or punchy effect.

Half-rhyme is when sounds are similar but not exact, such as "shape" and "keep". Half-rhyme can feel unsettled, unresolved or subtle.

Regular rhyme often suggests order, control or harmony. Irregular rhyme can suggest disorder, emotional disruption or freedom.
`,
      },

      interactiveLesson: {
        title: 'Rhyme Schemes',
        steps: [
          question('Two rhyming lines in a row form a:', ['Rhyming couplet', 'Haiku'], 0),
          question('An AABB scheme has:', ['Two pairs of rhyming couplets', 'Alternating rhymes'], 0),
          question('"Cat", "hat", "mat" share a:', ['Rhyme', 'Syllable count'], 0),
          question('ABAB is usually called:', ['Alternate rhyme', 'Envelope rhyme'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
A poem uses the scheme ABAB, ABAB, ABAB, CC.

Model answer:
The poem uses three regular ABAB quatrains, followed by a final rhyming couplet. The regular rhyme creates a controlled, musical quality, while the final couplet gives the poem a strong sense of closure. This structure is often associated with Shakespearean sonnets.
`,
      },

      practice: [
        mcq('A regular rhyme scheme usually creates:', ['A steady, musical feel', 'A random, chaotic feel'], 0),
        tf('Irregular rhyme can reflect disorder.', true),
        tf('A rhyming couplet has three lines.', false),
        mcq('ABBA is known as:', ['Envelope rhyme', 'Free verse'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write a rhyming couplet about a stormy night.
`,
        sentenceStarter: '',
        hints: [
          'Both lines should end with rhyming words.',
          'Keep the lines close in length.',
          'Use one strong image.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Writes two lines',
          'Uses matching end sounds',
          'Creates a clear image',
          'Maintains rhythm as far as possible',
        ],
      },

      examinerTip:
        'Do not just label the rhyme scheme. Explain whether the pattern creates control, closure, tension, humour or disorder.',

      commonMistakes: [
        'Confusing rhyme with rhythm',
        'Forgetting half-rhyme',
        'Labelling a rhyme scheme incorrectly',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your couplet by making the final rhyming words more powerful or surprising.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
What is the effect of placing a rhyming couplet at the end of a sonnet?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-13',
      slug: 'rhythm',
      title: 'Rhythm',
      summary: 'Metre, syllables, caesura and enjambment.',

      keyTerms: [
        'rhythm',
        'metre',
        'syllable',
        'stress',
        'caesura',
        'enjambment',
        'pace',
        'line break',
      ],

      explainer: {
        title: 'Rhythm',
        content: `
Rhythm is the pattern of beats in poetry. It affects how a poem sounds and how quickly or slowly it moves.

Key terms:
- Syllable: one beat in a word
- Stress: emphasis placed on a syllable
- Metre: a regular pattern of stressed and unstressed syllables
- Caesura: a pause in the middle of a line, often created by punctuation
- Enjambment: when a sentence runs over from one line to the next without a pause
- Line break: where a line ends

Effects:
- Regular rhythm can feel calm, controlled, song-like or mechanical
- Irregular rhythm can feel disturbed, excited or tense
- Caesura can create shock, hesitation or emphasis
- Enjambment can create flow, speed or momentum

When analysing rhythm, read the line aloud. Your ear will often notice what your eye misses.
`,
      },

      interactiveLesson: {
        title: 'Rhythm',
        steps: [
          question('A pause in the middle of a line is called:', ['Caesura', 'Enjambment'], 0),
          question('A sentence running over line breaks with no pause is:', ['Enjambment', 'A rhyming couplet'], 0),
          question('Metre is the pattern of:', ['Stressed and unstressed syllables', 'Full stops'], 0),
          question('A line break is:', ['Where a line ends', 'A spelling mistake'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
The road wound on. Long. Empty. Dark.

Model answer:
The poet uses caesura and one-word sentences to create a slow, heavy rhythm. Each full stop forces the reader to pause, making the road feel endless. The isolated words "Long. Empty. Dark." also visually and rhythmically reflect loneliness.
`,
      },

      practice: [
        mcq('Enjambment often makes a poem feel:', ['Flowing', 'Choppy'], 0),
        tf('Metre is the same as rhyme.', false),
        tf('Caesura can slow the reader down.', true),
        mcq('A regular rhythm can create a feeling of:', ['Control', 'Random spelling'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write two poetic lines about a river that use enjambment.
`,
        sentenceStarter: '',
        hints: [
          'Start a sentence on line 1 and finish it on line 2.',
          'Do not use a full stop at the line break.',
          'Try to make the movement feel flowing.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses clear enjambment',
          'Creates a flowing rhythm',
          'Uses river imagery',
        ],
      },

      examinerTip:
        'Rhythm analysis becomes stronger when you link sound to meaning: does the rhythm imitate movement, emotion or conflict?',

      commonMistakes: [
        'Confusing caesura and enjambment',
        'Ignoring punctuation',
        'Only counting syllables without explaining effect',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your lines by reading them aloud and adjusting the line break so the rhythm better matches the movement of the river.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might a poet use caesura to slow down an otherwise fast-paced poem?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-14',
      slug: 'poetic-form-and-structure',
      title: 'Poetic Form and Structure',
      summary: "How a poem's form shapes its structure and meaning.",

      keyTerms: [
        'form',
        'structure',
        'volta',
        'stanza',
        'line length',
        'free verse',
        'sonnet',
        'ballad',
      ],

      explainer: {
        title: 'Poetic Form and Structure',
        content: `
Form is the type of poem. Structure is how the poem is organised.

Form might include:
- Sonnet
- Ballad
- Haiku
- Dramatic monologue
- Free verse
- Elegy
- Narrative poem

Structure includes:
- Number and length of stanzas
- Line lengths
- Rhyme scheme
- Rhythm
- Enjambment and caesura
- Repetition
- Order of ideas
- Shifts in mood or argument

A volta is a turn or shift in thought, often found in sonnets. It may mark a change in emotion, argument or perspective.

The form and structure should connect to meaning. A poem about grief might use fragmented lines. A poem about control might use strict rhyme and regular rhythm. A poem about freedom might use free verse.

Always ask:
How does the shape of the poem support its meaning?
`,
      },

      interactiveLesson: {
        title: 'Form and Structure',
        steps: [
          question('A sonnet has how many lines?', ['14', '10'], 0),
          question('Free verse has:', ['No fixed rhyme or metre', 'A strict AABB pattern'], 0),
          question('The shift of idea in a sonnet is called the:', ['Volta', 'Couplet'], 0),
          question('Structure includes:', ['The order of ideas', 'Only the title'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
A poem about loss has short, broken lines and no rhyme scheme.

Model answer:
The irregular structure mirrors the subject of loss. The short, broken lines feel halting and emotional, as if the speaker cannot express themselves fluently. The lack of rhyme prevents the poem from sounding neat or resolved, reflecting how grief often feels unfinished.
`,
      },

      practice: [
        mcq('A ballad usually tells:', ['A story', 'A single statistic'], 0),
        tf("A poem's structure can reflect its theme.", true),
        tf('Free verse means the poem has no structure at all.', false),
        mcq('A volta is:', ['A turn in thought or argument', 'A spelling pattern'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Pick a theme, such as hope, fear, grief or freedom. Describe the form and structure you would use for a poem on that theme, explaining why.
`,
        sentenceStarter: 'For a poem about ___, I would use...',
        hints: [
          'Mention line length.',
          'Mention rhyme or lack of rhyme.',
          'Explain how your choices fit the theme.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Chooses a form',
          'Describes the structure',
          'Links choices to theme',
          'Explains intended reader effect',
        ],
      },

      examinerTip:
        'A confident poetry answer often includes one sentence on form or structure, not just language.',

      commonMistakes: [
        'Using form and structure as if they mean exactly the same thing',
        'Ignoring shifts in the poem',
        'Saying free verse has no structure',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining where you would place a shift or volta and why.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why does breaking a strict form halfway through a poem sometimes create a powerful effect?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-15',
      slug: 'answering-questions-about-poetry',
      title: 'Answering Questions About Poetry',
      summary: 'Link language, structure and theme to the question.',

      keyTerms: [
        'theme',
        'speaker',
        'language',
        'structure',
        'form',
        'effect',
        'interpretation',
        'quotation',
      ],

      explainer: {
        title: 'Answering Questions About Poetry',
        content: `
Poetry questions usually ask how the poet presents a theme, feeling, speaker or idea.

A strong poetry answer:
- Begins with a clear overview
- Uses short embedded quotations
- Comments on language
- Comments on structure or form
- Explains effect on the reader
- Links back to the question throughout
- Offers an interpretation rather than a single fixed meaning

Useful phrases:
- The poet presents...
- The image of...
- The repeated phrase...
- The line break after...
- The shift in the final stanza...
- This suggests...
- This could imply...
- The reader is left with...

Avoid writing:
- "This makes the reader want to read on" for every effect
- "The poet uses a technique" without explaining it
- Long biography instead of poem analysis
`,
      },

      interactiveLesson: {
        title: 'Strong Poetry Answers',
        steps: [
          question('A strong answer should comment on:', ['Language and structure', 'Only rhyme'], 0),
          question('Every point should link back to:', ['The question', 'Your favourite poem'], 0),
          question('Instead of listing techniques, you should:', ['Explain their effect', 'Name more techniques'], 0),
          question('A poetry overview should:', ['Give a clear interpretation', 'Retell every line'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does the poet create a feeling of hope?

Text:
And still, the dawn.

Model answer:
The poet creates a feeling of hope through the image of "dawn", which symbolises new beginnings. Placing "dawn" at the end of a very short line gives the word extra weight. The phrase "And still" suggests endurance, implying that hope continues even after difficulty.
`,
      },

      practice: [
        mcq('What does "dawn" often symbolise?', ['Hope and new beginnings', 'Defeat'], 0),
        tf('Strong poetry answers explain why a technique was used.', true),
        tf('You should ignore line breaks in poetry.', false),
        mcq(
          'Which is more analytical?',
          [
            'The line break isolates "dawn", emphasising hope.',
            'The poem has a word at the end.',
          ],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"The sea forgets. The sea remembers."

Write a short paragraph analysing how the poet creates their effect.
`,
        sentenceStarter: 'The poet creates...',
        hints: [
          'Comment on repetition.',
          'Comment on the short sentence structure.',
          'Offer more than one interpretation if possible.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Makes a clear point',
          'Uses a short quotation',
          'Comments on language',
          'Comments on structure',
          'Links to theme or effect',
        ],
      },

      examinerTip:
        'In poetry, placement matters. A word at the end of a line or stanza often receives extra emphasis.',

      commonMistakes: [
        'Only writing about language',
        'Ignoring the speaker',
        'Treating one interpretation as the only possible meaning',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence that begins: "Structurally, the poet..."
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is it useful to comment on the position of a word at the start, middle or end of a line?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-16',
      slug: 'play-features',
      title: 'Play Features',
      summary: 'Scripts, stage directions, dialogue, monologues and asides.',

      keyTerms: [
        'script',
        'stage direction',
        'dialogue',
        'monologue',
        'soliloquy',
        'aside',
        'act',
        'scene',
        'dramatic irony',
      ],

      explainer: {
        title: 'Play Features',
        content: `
A play is fiction written to be performed live. This means the audience experiences it through speech, movement, staging, sound and visual choices.

Key features:
- Script: the written text of the play
- Dialogue: characters speaking to each other
- Stage directions: instructions for movement, expression, setting or delivery
- Monologue: one character speaking at length
- Soliloquy: a character speaking their thoughts aloud, often alone on stage
- Aside: a comment heard by the audience but not by other characters onstage
- Act: a large section of a play
- Scene: a smaller section, often in one place or moment

Plays create meaning through performance. A line can change meaning depending on:
- Tone of voice
- Pause
- Gesture
- Facial expression
- Distance between characters
- Lighting and sound

When analysing plays, do not treat them exactly like novels. Always imagine the scene on stage.
`,
      },

      interactiveLesson: {
        title: 'Play Parts',
        steps: [
          question('The written text of a play is called the:', ['Script', 'Act'], 0),
          question('One character speaking for a long time is a:', ['Monologue', 'Dialogue'], 0),
          question(
            'A character speaking thoughts that only the audience hears is an:',
            ['Aside', 'Act'],
            0
          ),
          question('A soliloquy often reveals:', ['Private thoughts', 'The page number'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
In many Shakespeare plays, characters speak asides directly to the audience while other characters remain present onstage.

Model answer:
The aside is powerful because it lets the audience hear a character’s private thoughts while other characters remain unaware. This can create dramatic irony because the audience knows more than the characters on stage. It can also make the audience feel like a character’s accomplice.
`,
      },

      practice: [
        mcq('Stage directions are usually written in:', ['Italics or brackets', 'Red marker'], 0),
        tf('Scenes are bigger than acts.', false),
        tf('A play is written to be performed.', true),
        mcq('Dramatic irony happens when:', ['The audience knows more than a character', 'Nobody knows anything'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Explain one difference between a monologue, a soliloquy and an aside.
`,
        sentenceStarter: 'A monologue is...',
        hints: [
          'Say who can hear it.',
          'Think about whether the character is alone.',
          'Think about the audience’s role.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Explains monologue correctly',
          'Explains soliloquy or aside correctly',
          'Shows the key difference',
          'Mentions audience awareness',
        ],
      },

      examinerTip:
        'In drama, the audience matters. Use “audience” rather than “reader” unless discussing the script as a printed text.',

      commonMistakes: [
        'Ignoring stage directions',
        'Confusing aside and soliloquy',
        'Writing about plays as if they are novels',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining how the audience’s experience changes when they hear private information.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why are stage directions often as important as the spoken lines?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-17',
      slug: 'characters-in-plays',
      title: 'Characters in Plays',
      summary: 'Appearance, dialogue, delivery and behaviour.',

      keyTerms: [
        'appearance',
        'costume',
        'dialogue',
        'delivery',
        'gesture',
        'body language',
        'foil',
        'relationship',
      ],

      explainer: {
        title: 'Characters in Plays',
        content: `
In plays, characters are revealed through what the audience sees and hears.

A playwright can reveal character through:
- Appearance and costume
- Dialogue
- Tone, pace and volume
- Movement and gesture
- Body language
- Stage directions
- Silence and pauses
- What other characters say
- How relationships change

Because plays are performed, non-verbal details can be extremely important. A character who says "I'm fine" while trembling is not presented in the same way as a character who says it calmly.

Look for:
- Entrances and exits
- Who controls the conversation
- Who interrupts
- Who stays silent
- How close characters stand to each other
- Changes in behaviour across the scene

Strong drama analysis links character to audience reaction.
`,
      },

      interactiveLesson: {
        title: 'Revealing Character',
        steps: [
          question('A character’s costume can tell the audience:', ['Status and personality', 'Nothing useful'], 0),
          question('Stage directions like "(whispers)" tell us about:', ['How a line is said', 'Who is in the audience'], 0),
          question('Strong play characters usually:', ['Have strengths and flaws', 'Are always perfect'], 0),
          question('Silence on stage can:', ['Create tension or reveal emotion', 'Never mean anything'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
A character enters wearing torn clothes and speaks quietly.

Model answer:
The torn costume immediately suggests poverty, conflict or hardship, making the audience feel sympathy before the character even speaks. The quiet delivery may imply fear, exhaustion or low confidence. Together, the visual and vocal details create a vulnerable first impression.
`,
      },

      practice: [
        mcq('Which channel can reveal a character in a few seconds?', ['Appearance', 'Rhyme scheme'], 0),
        tf('Strong characters always stay the same throughout a play.', false),
        tf('Body language can reveal emotion.', true),
        mcq(
          'A character who contrasts with another character is called:',
          ['A foil', 'A stanza'],
          0
        ),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Explain two ways a playwright can reveal a character to the audience without the character speaking.
`,
        sentenceStarter: 'A playwright can reveal a character by...',
        hints: [
          'Think about costume and body language.',
          'Think about actions or silence.',
          'Think about what others say.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Names two non-verbal methods',
          'Explains what each reveals',
          'Links to audience reaction',
        ],
      },

      examinerTip:
        'In drama, silence can be evidence. A pause, hesitation or refusal to answer may reveal power, fear or guilt.',

      commonMistakes: [
        'Only analysing spoken lines',
        'Forgetting costume and movement',
        'Ignoring relationships between characters',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one stage direction that would reveal a character’s emotion without using dialogue.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is a character with both strengths and flaws more interesting than a purely heroic one?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-18',
      slug: 'staging',
      title: 'Staging',
      summary: "Set, props, lighting, sound and the director's choices.",

      keyTerms: [
        'staging',
        'set',
        'props',
        'lighting',
        'sound',
        'costume',
        'blocking',
        'director',
        'minimalism',
      ],

      explainer: {
        title: 'Staging',
        content: `
Staging is how a play is presented onstage.

Staging includes:
- Set: scenery and stage environment
- Props: objects used by actors
- Lighting: brightness, colour, shadow and focus
- Sound: music, effects, silence and volume
- Costume: clothing, status and symbolism
- Blocking: where actors stand and move
- Proxemics: the distance between characters
- Entrances and exits
- Use of space

Directors can interpret the same play in different ways. A Shakespeare play could be staged in Elizabethan costume, modern clothing, a war zone, a school, a prison, or a bare stage. Each choice changes meaning.

When analysing staging, ask:
- What does the audience see?
- What does the audience hear?
- Where is attention directed?
- What mood is created?
- What does the staging suggest about power, conflict or emotion?
`,
      },

      interactiveLesson: {
        title: 'Staging Choices',
        steps: [
          question('An object held by an actor onstage is a:', ['Prop', 'Set piece'], 0),
          question('Red lighting would most likely suggest:', ['Danger or anger', 'Peace'], 0),
          question('Two directors staging the same play:', ['Can interpret it differently', 'Must produce identical shows'], 0),
          question('Blocking means:', ['Where actors stand and move', 'The rhyme pattern'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
A director stages the final scene of Macbeth with a single spotlight on Macbeth and the rest of the stage in darkness.

Model answer:
The staging isolates Macbeth visually, reflecting how alone he has become. The darkness around him suggests he is cut off from help, while the single spotlight forces the audience to focus on his reaction. This makes the final moment feel tragic and tense.
`,
      },

      practice: [
        mcq('Which of these is part of staging?', ['Lighting', 'Rhyme scheme'], 0),
        tf('Every performance of a play must look the same.', false),
        tf('Silence can be a sound choice in staging.', true),
        mcq('The distance between characters can suggest:', ['Power or intimacy', 'A spelling rule'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Describe a staging choice — set, lighting, prop, costume, movement or sound — for a tense scene and explain its effect on the audience.
`,
        sentenceStarter: 'I would stage this scene by...',
        hints: [
          'Pick one element.',
          'Link it clearly to the emotion you want.',
          'Mention what the audience sees or hears.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Names a staging choice',
          'Links it to the intended emotion',
          'Shows awareness of the audience',
          'Explains meaning rather than just appearance',
        ],
      },

      examinerTip:
        'A staging answer is strongest when it links a visual or sound choice to the audience’s interpretation of character, power or theme.',

      commonMistakes: [
        'Describing staging without effect',
        'Forgetting sound and silence',
        'Ignoring actor movement',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining how your staging choice changes the audience’s understanding of the scene.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might a director choose a completely bare stage for an emotional scene?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-19',
      slug: 'answering-questions-about-plays',
      title: 'Answering Questions About Plays',
      summary: 'Combine dialogue, stage directions and audience effect.',

      keyTerms: [
        'playwright',
        'audience',
        'stage direction',
        'dialogue',
        'performance',
        'dramatic irony',
        'context',
      ],

      explainer: {
        title: 'Answering Questions About Plays',
        content: `
Play-based questions usually ask how a playwright presents a character, relationship, theme or scene.

A strong answer:
- Treats the play as performance
- Reads stage directions carefully
- Uses short quotations from dialogue
- Uses stage directions as evidence
- Comments on the audience’s reaction
- Explains how the scene would look and sound
- Links to context where relevant

Useful phrases:
- The playwright presents...
- The stage direction "..." suggests...
- The audience is positioned to...
- On stage, this would make...
- The pause creates...
- The contrast between speech and action suggests...

For Shakespeare, context might include:
- Elizabethan/Jacobean beliefs
- Gender expectations
- Kingship and power
- Religion and fate
- The Globe and live audience response
`,
      },

      interactiveLesson: {
        title: 'Writing Play Answers',
        steps: [
          question('For a play, you should quote from:', ['Dialogue and stage directions', 'Dialogue only'], 0),
          question('When analysing a play, always consider:', ['The audience', 'Only the reader'], 0),
          question('Context for Shakespeare may include:', ['Elizabethan theatre and society', 'Modern football'], 0),
          question('A strong play answer should imagine:', ['The scene onstage', 'Only the printed page'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does the playwright create tension between two characters?

Text:
(coldly) I thought we had an understanding.
Did you?

Model answer:
The playwright creates tension through the stage direction "(coldly)", which tells the actor to deliver the line without warmth. The short question "Did you?" throws the accusation back at the first speaker, making the exchange feel confrontational. On stage, the audience would sense the emotional distance between the characters.
`,
      },

      practice: [
        mcq('In play analysis, "(coldly)" is:', ['A stage direction', 'A rhyme'], 0),
        tf("In play answers, you should comment on the audience's reaction.", true),
        tf('Stage directions cannot be used as evidence.', false),
        mcq('The playwright is:', ['The writer of the play', 'The main prop'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this extract:

(steps back) You cannot mean that.
I do.

Write a short analytical paragraph explaining how the playwright creates drama.
`,
        sentenceStarter: 'The playwright creates drama by...',
        hints: [
          'Quote the stage direction.',
          'Quote a short line of dialogue.',
          'Comment on the audience.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Quotes both dialogue and a stage direction',
          'Links to the audience',
          'Explains the effect clearly',
          'Treats the scene as performance',
        ],
      },

      examinerTip:
        'Use “playwright” and “audience” in drama answers. It signals that you understand the form.',

      commonMistakes: [
        'Calling the audience the reader throughout',
        'Ignoring performance choices',
        'Only analysing dialogue',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your paragraph by adding one sentence beginning: "On stage, this would..."
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is imagining a scene onstage often more powerful than reading it as a book?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-20',
      slug: 'writing-poetry-and-plays',
      title: 'Writing Poetry and Plays',
      summary: 'Plan, craft language, and build mood onstage or on the page.',

      keyTerms: [
        'form',
        'imagery',
        'rhythm',
        'dialogue',
        'stage direction',
        'conflict',
        'subtext',
        'silence',
      ],

      explainer: {
        title: 'Writing Poetry and Plays',
        content: `
Writing poetry and plays requires control. Both forms depend on precise choices.

When writing a poem:
- Choose a subject, mood and speaker
- Decide on form: free verse, sonnet, haiku, narrative poem, etc.
- Use imagery and sound deliberately
- Decide whether to use rhyme
- Use line breaks for emphasis
- Read aloud to test rhythm

When writing a play:
- Choose a clear setting and conflict
- Keep the cast small
- Use dialogue to reveal character
- Use stage directions to control movement, tone and mood
- Include subtext: what characters really mean beneath what they say
- Use silence and pauses for tension

In both forms, cut anything that does not add to mood, meaning or character.
`,
      },

      interactiveLesson: {
        title: 'Writing Tips',
        steps: [
          question('Before writing a poem or script, you should:', ['Plan', 'Dive in with no idea'], 0),
          question('A short play usually focuses on:', ['A narrow cast of 2-4 characters', '20+ characters'], 0),
          question('Reading poetry aloud helps you check:', ['Rhythm', 'Font size'], 0),
          question('Subtext means:', ['What is meant beneath the words', 'The title of a textbook'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Task:
Write a short scene set in a hospital corridor at night.

Model:
(LILY sits on a bench under a flickering light. The corridor is empty.)

LILY: (quietly) I thought he'd be awake by now.

(Footsteps. SAM enters, slowly.)

SAM: You should go home.

LILY: I can't.

Why it works:
The stage directions control mood through the "flickering light" and empty corridor. The dialogue is short, natural and emotionally loaded. The focus stays on two characters and one tense moment.
`,
      },

      practice: [
        mcq('When writing a script, short dialogue can feel:', ['Tense and real', 'Always dull'], 0),
        tf('Stage directions are optional extras with no effect.', false),
        tf('Poetry should be read aloud to test sound and rhythm.', true),
        mcq('A strong short scene usually needs:', ['Conflict or tension', 'A huge cast'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write the first four lines of a short play between two characters waiting for bad news. Include at least one stage direction.
`,
        sentenceStarter: '(Two people sit in silence.)\nA:',
        hints: [
          'Keep lines short.',
          'Use a stage direction like "(looks away)".',
          'Let the characters avoid saying everything directly.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses at least one stage direction',
          'Dialogue feels natural',
          'Creates a clear mood',
          'Suggests subtext',
        ],
      },

      examinerTip:
        'In scripts, what is not said can be as important as what is said. Silence, hesitation and short replies can create tension.',

      commonMistakes: [
        'Writing dialogue that explains everything',
        'Forgetting stage directions',
        'Making poems vague rather than precise',
        'Using rhyme that sounds forced',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your script by cutting one over-explained line and replacing it with a pause, gesture or short reply.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why does less dialogue and more silence often feel more powerful onstage?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-21',
      slug: 'warm-up-and-worked-practice-questions-fiction',
      title: 'Warm-Up and Worked Practice Questions',
      summary: 'Scaffolded practice for fiction, poetry and plays.',

      keyTerms: [
        'warm-up',
        'method',
        'effect',
        'evidence',
        'reader',
        'audience',
        'analysis',
      ],

      explainer: {
        title: 'Warm-Up and Worked Practice',
        content: `
Warm-up questions help you practise the small skills that lead to strong long answers.

This topic practises:
- Identifying methods
- Selecting short evidence
- Explaining effect
- Distinguishing reader and audience
- Commenting on language and structure
- Moving from "what happens" to "how it is written"

A worked practice answer is not something to memorise word for word. Instead, study how it works:
- It starts with a clear point
- It uses short evidence
- It zooms in on a word or structural choice
- It explains the effect
- It links back to the question

Use this as low-stakes training before longer exam-style answers.
`,
      },

      interactiveLesson: {
        title: 'Warm-Up',
        steps: [
          question('"The wind howled" is an example of:', ['Personification', 'Rhyme'], 0),
          question('An AABB scheme is:', ['Two pairs of rhyming couplets', 'Random rhyme'], 0),
          question('Stage directions tell actors:', ['How to deliver lines or move', 'Who is in the audience'], 0),
          question('In a play answer, use:', ['Audience', 'Reader only'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Practice Question',
        content: `
Question:
How does the writer create tension in this short passage?

Text:
The door handle turned. Slowly. Then stopped.

Model answer:
The writer creates tension through very short sentences, which slow the pace. The one-word sentence "Slowly" forces the reader to pause, imitating the slow movement of the handle. The final sentence "Then stopped" leaves the moment unresolved, making the reader wonder who is on the other side.
`,
      },

      practice: [
        mcq('Why is "Slowly" powerful as a one-word sentence?', ['It forces the reader to pause', 'It adds rhyme'], 0),
        tf('An unresolved ending can create tension.', true),
        tf('A warm-up answer should still use evidence.', true),
        mcq('The final sentence "Then stopped" creates:', ['Suspense', 'Certainty'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"The clock kept ticking. Too loud."

Write a short paragraph analysing how the writer creates mood.
`,
        sentenceStarter: 'The writer creates...',
        hints: [
          'Comment on the short sentence.',
          'Comment on the word "too".',
          'Explain why the sound feels uncomfortable.',
        ],
      },

      aiFeedback: {
        successCriteria: analysisSuccess,
      },

      examinerTip:
        'Even a short answer should include method and effect. Do not wait until long questions to practise analysis.',

      commonMistakes: [
        'Only identifying the technique',
        'Forgetting to explain the word choice',
        'Not linking to mood',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your paragraph by adding one sentence about why the ticking feels louder than it should.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why are one-word sentences so often used in tense scenes?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-22',
      slug: 'worked-practice-questions-fiction',
      title: 'Worked Practice Questions',
      summary: 'Modelled analytical responses across fiction, poetry and plays.',

      keyTerms: [
        'model answer',
        'overview',
        'embedded quotation',
        'zoom-in',
        'interpretation',
        'purpose',
      ],

      explainer: {
        title: 'Worked Practice Questions',
        content: `
A worked practice question shows how a strong answer is built.

Study model answers for:
- The opening overview sentence
- How each paragraph makes one clear point
- How quotations are short and embedded
- How single words are analysed
- How language and structure are connected
- How the final explanation links back to the question
- How the answer comments on reader or audience effect

A strong model answer often follows this structure:
1. Overview: a direct answer to the question
2. Method: language, structure, form or staging
3. Evidence: short quotation or precise reference
4. Analysis: zoom in and explain effect
5. Purpose: link to theme, context or writer’s intention

Do not copy models mechanically. Learn the habits behind them.
`,
      },

      interactiveLesson: {
        title: 'Study the Model',
        steps: [
          question('A strong model answer usually opens with:', ['A confident overview sentence', 'A long introduction'], 0),
          question('Zooming in on a single word:', ['Shows precise analysis', 'Wastes time'], 0),
          question('Quotations work best when they are:', ['Short and embedded', 'Long and stand-alone'], 0),
          question('A final explanation should link to:', ['The question or writer’s purpose', 'A random new idea'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Model Answer',
        content: `
Question:
How does the writer present a sense of decline?

Text:
The old gates hung at odd angles, and weeds had swallowed the path.

Model answer:
The writer presents a strong sense of decline through images of damage and neglect. The phrase "hung at odd angles" makes the gates seem broken and unstable, suggesting the place has lost its former order. The verb "swallowed" in "weeds had swallowed the path" personifies nature as a force that has taken control. Together, these details imply the setting has been abandoned for a long time.
`,
      },

      workedExamples: [
        tier(
          'Secure',
          'The place is old and abandoned because the gates are broken.',
          'This is relevant but needs closer word analysis.'
        ),
        tier(
          'Strong',
          'The verb "swallowed" personifies the weeds, making nature seem powerful and invasive.',
          'This analyses word choice effectively.'
        ),
        tier(
          'Grade 9 move',
          'The path being "swallowed" suggests more than physical neglect: the route into the place has been erased, symbolising a collapse of connection with the past.',
          'This adds symbolic interpretation.'
        ),
      ],

      practice: [
        mcq('The verb "swallowed" suggests nature has:', ['Taken over', 'Been mowed down'], 0),
        tf('Strong model answers always link back to the question.', true),
        tf('An overview sentence should retell the whole plot.', false),
        mcq('A precise quotation is usually:', ['Short', 'A whole paragraph'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"The house waited, its windows like closed eyes."

Write a short model-style paragraph analysing the writer's presentation of the house.
`,
        sentenceStarter: 'The writer presents...',
        hints: [
          'Comment on personification.',
          'Comment on simile.',
          'Zoom in on "waited" or "closed".',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Opens with an overview',
          'Uses a short quotation',
          'Zooms in on a word',
          'Explains writer purpose or effect',
        ],
      },

      examinerTip:
        'Model answers are useful because they show habits: precision, relevance, quotation control and explanation of effect.',

      commonMistakes: [
        'Copying a model without understanding it',
        'Using too many quotations',
        'Not linking the final sentence back to the question',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your paragraph by adding one symbolic interpretation of the house.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why do examiners reward answers that link to the writer's bigger purpose?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-23',
      slug: 'practice-questions-fiction',
      title: 'Practice Questions',
      summary: 'Independent practice for fiction, poetry and play analysis.',

      keyTerms: [
        'independent practice',
        'planning',
        'annotation',
        'evidence',
        'method',
        'effect',
        'purpose',
      ],

      explainer: {
        title: 'Practice Questions',
        content: `
Independent practice is where you turn knowledge into skill.

Before writing:
- Read the question carefully
- Highlight the focus word
- Read the extract twice
- Choose two or three short quotations
- Plan one point per paragraph

While writing:
- Begin with a clear overview
- Use short evidence
- Explain methods and effects
- Use subject terminology accurately
- Link back to the question
- Mention reader or audience depending on form

After writing:
- Check every paragraph answers the question
- Check quotations are short
- Check you have explained, not just labelled
- Check punctuation and spelling

For fiction, use "writer" and "reader".
For poetry, use "poet", "speaker" and "reader".
For plays, use "playwright" and "audience".
`,
      },

      interactiveLesson: {
        title: 'Exam Habits',
        steps: [
          question('Before writing, you should:', ['Plan', 'Start writing immediately'], 0),
          question('Every sentence of your answer should:', ['Be relevant to the question', 'Be long'], 0),
          question('Strong answers name techniques and:', ['Explain their effect', 'Only list them'], 0),
          question('For plays, you should write about the:', ['Audience', 'Reader only'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does the writer present the character's fear?

Text:
She gripped the banister until her knuckles turned white.

Model answer:
The writer presents the character's fear through physical detail. The verb "gripped" suggests tension and panic, while "knuckles turned white" shows the intensity of her fear through her body. The writer does not need to state that she is afraid; the physical reaction allows the reader to infer it.
`,
      },

      practice: [
        mcq('The detail "knuckles turned white" is effective because it:', ['Shows fear through the body', 'Describes the weather'], 0),
        tf('Good answers explain effect, not just list techniques.', true),
        tf('You should use the longest quotation possible.', false),
        mcq('Inference means:', ['Working out what is suggested', 'Copying the question'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"He smiled, but his eyes did not move."

Write a short paragraph analysing how the writer creates unease.
`,
        sentenceStarter: 'The writer creates unease by...',
        hints: [
          'Use a short quotation.',
          'Comment on the contrast.',
          'Explain why the smile feels unnatural.',
        ],
      },

      aiFeedback: {
        successCriteria: analysisSuccess,
      },

      examinerTip:
        'Independent practice is where you should deliberately practise one improvement target at a time, such as better evidence or stronger zoom-ins.',

      commonMistakes: [
        'Forgetting to plan',
        'Using the same phrase repeatedly',
        'Writing about what happens instead of how it is written',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your paragraph by adding one sentence beginning: "The contrast suggests..."
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is contrast, such as a smile with no emotion, such a strong analytical point to notice?
`,
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-3-24',
      slug: 'reading-and-writing-fiction-knowledge-organiser',
      title: 'Reading & Writing Fiction — Knowledge Organiser',
      summary: 'Key knowledge summary for fiction, poetry and plays.',

      keyTerms: [
        'setting',
        'theme',
        'characterisation',
        'mood',
        'structure',
        'narrator',
        'form',
        'rhyme',
        'rhythm',
        'staging',
        'audience',
        'purpose',
      ],

      explainer: {
        title: 'Knowledge Organiser',
        content: `
Use this summary to revise Section 3.

Fiction:
- Setting = where and when a story happens.
- Theme = what the text is really about beneath the plot.
- Characterisation = how a writer presents characters.
- Mood = the emotional atmosphere.
- Structure = how the text is organised.
- Narrative viewpoint controls what the reader knows and trusts.
- Strong fiction analysis links method, evidence, effect and purpose.

Novel techniques:
- Protagonist
- Antagonist
- Dynamic character
- Foreshadowing
- Flashback
- Cliffhanger
- Turning point
- Unreliable narrator

Story writing:
- Plan before writing.
- Keep the focus narrow.
- Open with a hook.
- Use sensory detail.
- Vary sentence length.
- End with control, not cliché.

Poetry:
- Lines form stanzas.
- Form = type of poem.
- Structure = organisation of ideas, lines and stanzas.
- Rhyme scheme creates pattern.
- Rhythm controls pace and sound.
- Caesura creates pause.
- Enjambment creates flow.
- Volta means a turn or shift.

Poetic techniques:
- Imagery
- Simile
- Metaphor
- Personification
- Repetition
- Alliteration
- Sibilance
- Onomatopoeia
- Symbolism

Drama:
- Plays are written for performance.
- Script = written text.
- Dialogue = speech between characters.
- Stage directions = performance instructions.
- Monologue = one character speaking at length.
- Aside = audience hears what others onstage do not.
- Staging includes set, props, lighting, sound, costume and movement.
- In drama answers, write about the audience.

Analytical answer checklist:
- Point
- Short evidence
- Method
- Effect
- Zoom-in
- Link to theme, purpose, context, reader or audience
`,
      },

      interactiveLesson: {
        title: 'Recall Quiz',
        steps: [
          question('Which of these is a structural technique?', ['Foreshadowing', 'Onomatopoeia'], 0),
          question('An aside is heard by:', ['The audience only', 'The whole cast'], 0),
          question('A 14-line poem is likely a:', ['Sonnet', 'Haiku'], 0),
          question('An unreliable narrator:', ['Gives a biased or incomplete account', 'Is always correct'], 0),
          question('Caesura is:', ['A pause in a line', 'A type of character'], 0),
          question('For plays, you should comment on the:', ['Audience', 'Reader only'], 0),
        ],
      },

      workedExample: {
        title: 'Worked Summary',
        content: `
Text:
The stage darkened. Only the clock remained lit. Tick. Tick. Tick.

Model answer:
This extract combines staging, sound and structure to build tension. The lighting focuses attention on the clock, making it symbolise time running out. The repeated "Tick" creates a harsh, regular sound, while the short sentences slow the pace and force the audience to wait anxiously.
`,
      },

      practice: [
        mcq('The clock in the extract could symbolise:', ['Time running out', 'Peacefulness'], 0),
        tf('A reliable narrator tells the truth as far as they know it.', true),
        tf('Stage directions should be ignored in drama analysis.', false),
        mcq('Enjambment creates:', ['Flow across line breaks', 'A character description'], 0),
        mcq('A writer’s purpose means:', ['Why the writer may have made a choice', 'The size of the font'], 0),
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Pick three ideas from Section 3 and explain how each one could improve your analytical or creative writing.
`,
        sentenceStarter: 'Firstly,...',
        hints: [
          'Use at least one technique name.',
          'Explain how each idea helps a reader or audience.',
          'Include one fiction, one poetry and one drama idea if possible.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Chooses three clear ideas',
          'Explains why each one helps',
          'Links to reader, audience or purpose',
          'Uses accurate subject terminology',
        ],
      },

      examinerTip:
        'The knowledge organiser is not just for memorising terms. Use it to practise explaining effects clearly.',

      commonMistakes: [
        'Knowing terms but not explaining them',
        'Confusing reader and audience',
        'Forgetting structure and form',
      ],

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your response by adding one sentence that links a technique to a writer’s purpose.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Write a short paragraph analysing any fiction, poem or play you know well. Use at least two techniques and one short quotation.
`,
        xpReward: 50,
      },
    }),
  ],
};

export const getEnglishContent3TopicCount = () => englishContent3.topics.length;

export default englishContent3;